'use client';

import { useState } from 'react';
import type { OutlinedInputProps } from '@mui/material/OutlinedInput';
import { Autocomplete, Box, Chip, TextField } from '@/primitives';
import { useMediaQuery } from '@mui/material';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';

/**
 * The full hint runs to 235px, and the field is only ~147px on a phone, so it
 * truncated to "e.g. 2022, 2023 o…" — useless as an example. The short form
 * still shows both accepted shapes: a single year and a range.
 */
const YEAR_HINT = 'e.g. 2022, 2023 or 2020-2023';
const YEAR_HINT_COMPACT = '2022, 2020-2023';

const YEAR_PATTERN = /^\d{4}$/;
const RANGE_PATTERN = /^(\d{4})-(\d{4})$/;

function isValidYearToken(token: string): boolean {
  if (YEAR_PATTERN.test(token)) return true;
  const rangeMatch = token.match(RANGE_PATTERN);
  if (rangeMatch && rangeMatch[1] && rangeMatch[2]) {
    const start = parseInt(rangeMatch[1], 10);
    const end = parseInt(rangeMatch[2], 10);
    return start <= end;
  }
  return false;
}

export function expandCalvingYearTokens(tokens: string[]): string[] {
  const years = new Set<string>();
  for (const token of tokens) {
    const rangeMatch = token.match(RANGE_PATTERN);
    if (rangeMatch && rangeMatch[1] && rangeMatch[2]) {
      const start = parseInt(rangeMatch[1], 10);
      const end = parseInt(rangeMatch[2], 10);
      for (let y = start; y <= end; y++) {
        years.add(String(y));
      }
    } else if (YEAR_PATTERN.test(token)) {
      years.add(token);
    }
  }
  return Array.from(years).sort();
}

interface CalvingYearChipInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  sx?: SxProps<Theme>;
}

export default function CalvingYearChipInput({
  value,
  onChange,
  label,
  sx,
}: CalvingYearChipInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const { breakpoints } = useTheme();
  const isCompact = useMediaQuery(breakpoints.down('sm'));
  const yearHint = isCompact ? YEAR_HINT_COMPACT : YEAR_HINT;
  const chips = value
    ? value
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean)
    : [];

  function commitToken(token: string, currentChips: string[]) {
    const trimmed = token.trim();
    if (!trimmed || !isValidYearToken(trimmed)) return false;

    const merged = [...currentChips, trimmed];
    const unique = Array.from(new Set(merged));
    setInputValue('');
    onChange(unique.join(','));
    return true;
  }

  function handleInputChange(_event: unknown, newInput: string) {
    const lastChar = newInput.slice(-1);
    if (lastChar === ',' || lastChar === ' ') {
      const token = newInput.slice(0, -1).trim();
      if (token && isValidYearToken(token)) {
        commitToken(token, chips);
        return;
      }
    }
    setInputValue(newInput);
  }

  function handleChange(_event: unknown, newChips: string[]) {
    const lastAdded = newChips[newChips.length - 1];
    if (lastAdded && !isValidYearToken(lastAdded.trim())) {
      return;
    }
    const cleaned = newChips.map((c) => c.trim()).filter(Boolean);
    const unique = Array.from(new Set(cleaned));
    onChange(unique.join(','));
  }

  return (
    <Box sx={{ flex: 1 }}>
      <Autocomplete
        multiple
        freeSolo
        options={[]}
        value={chips}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleChange}
        onBlur={() => commitToken(inputValue, chips)}
        renderValue={(tagValues, getItemProps) =>
          tagValues.map((option, index) => {
            const { key, ...tagProps } = getItemProps({ index });
            return <Chip key={key} label={option} size="small" {...tagProps} />;
          })
        }
        renderInput={(params) => {
          const inputProps = params.slotProps.input as OutlinedInputProps;
          return (
            <TextField
              {...params}
              label={label}
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  onFocus: (
                    e: Parameters<
                      NonNullable<OutlinedInputProps['onFocus']>
                    >[0],
                  ) => {
                    setIsFocused(true);
                    inputProps.onFocus?.(e);
                  },
                  onBlur: (
                    e: Parameters<NonNullable<OutlinedInputProps['onBlur']>>[0],
                  ) => {
                    setIsFocused(false);
                    inputProps.onBlur?.(e);
                  },
                },
                inputLabel: {
                  ...params.slotProps.inputLabel,
                  // Same as sire/dam outlined fields: label in the outline until focus or value.
                  shrink:
                    chips.length > 0 || inputValue.length > 0 || isFocused,
                },
              }}
              placeholder={chips.length === 0 ? yearHint : ''}
              size="small"
            />
          );
        }}
        sx={sx}
      />
    </Box>
  );
}
